// coachcannon#662 — what the CQ product take page SHOWS, corrected without
// touching what the form STORES.
//
// Two strings in the vendored CQ product form reached respondents as they
// should not:
//
//   1. ATT-IMC-01's title opens with "ATTENTION CHECK:", an internal label shown
//      to the participant (2026-09-30 internal-labels rule).
//   2. The closing page said "Your responses have been recorded." BEFORE the
//      Complete button, so a respondent could read it as done, close the tab,
//      and not complete.
//
// The seeds are sha256-pinned by CqProductForms, and CLAUDE.md trap 6 rules out
// rewriting CQ quiz JSON in place; a content fix would be a new form version and
// a new survey. So the correction is presentation-only, applied in memory to the
// JSON SurveyTaker hands to `new Model(...)`, on the cq_product path only. What
// a respondent sees therefore differs from the seed in exactly these two
// strings, which the psychometrics record needs to know (#662).
//
// Answers: SurveyJS stores each answer under the question's `name` (or its
// `valueName`, which neither CQ seed sets), not under its title or html. This
// module edits titles and one html string; names and values are left as they
// were. __tests__/SurveyTaker.cqProductCopy.test.jsx pins that the completed
// payload equals the non-CQ render's for both registered seeds.
//
// Wording lives in the two constants below; a copy change is a one-line edit.

// Stripped from the START of a question title only, with the whitespace after
// it. Matched as the seed writes it (upper case, colon).
export const CQ_INTERNAL_TITLE_LABEL = /^\s*ATTENTION CHECK:\s*/;

// Replaces the closing page's html. Same box as the stored element (style copied
// from both seeds), so only the words change.
export const CQ_CLOSING_HTML =
  "<div style='padding:16px;background:#e8f5e9;border-radius:6px;'><h4>That's everything — thank you.</h4><p>Select <strong>Complete</strong> below to submit your assessment.</p></div>";

function stripInternalLabel(title) {
  if (typeof title !== 'string' || !CQ_INTERNAL_TITLE_LABEL.test(title)) return title;
  const shown = title.replace(CQ_INTERNAL_TITLE_LABEL, '');
  // SurveyJS shows the question NAME when a title is empty, and for this item
  // the name is an internal id (ATT-IMC-01). Keep the stored title instead.
  return shown === '' ? title : shown;
}

function relabel(elements) {
  if (!Array.isArray(elements)) return;
  elements.forEach((element) => {
    if (!element || typeof element !== 'object') return;
    if ('title' in element) element.title = stripInternalLabel(element.title);
    relabel(element.elements); // panels
    relabel(element.templateElements); // dynamic panels
  });
}

// The closing message is picked out by the form's SHAPE, not by an element
// name: the last page, when its only element is an html element. That matches
// product-v1.1.1 (P2_close / P2_close_html) and product-v3.0.0 (P3_close /
// P3_close_html), and CqProductServeTransform keeps it last: it re-pages the
// P1_battery page and splices the pages after it back unchanged. A last page
// that also carries a question is not treated as a closing page.
function closingElement(pages) {
  const last = pages[pages.length - 1];
  const elements = last && last.elements;
  if (!Array.isArray(elements) || elements.length !== 1) return null;
  const only = elements[0];
  return only && only.type === 'html' ? only : null;
}

// Returns a copy; the object passed in is left as it was.
export function presentCqProductQuiz(quizJson) {
  if (!quizJson || typeof quizJson !== 'object' || !Array.isArray(quizJson.pages)) return quizJson;

  // The form arrives as JSON in the react_component props, so a JSON round
  // trip copies it faithfully.
  const shown = JSON.parse(JSON.stringify(quizJson));

  shown.pages.forEach((page) => relabel(page && page.elements));

  const closing = closingElement(shown.pages);
  if (closing) closing.html = CQ_CLOSING_HTML;

  return shown;
}
